// --- React & Redux
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
// --- Utils
import { fetchReadyProductsPage } from '../../../utils/fetchReadyProductsPage';
// --- Actions & Hooks
import { useAgGridApi } from 'datastage/components/AGGrid/hooks';
import { useReadyProductsActions } from 'datastage/store/readyProducts/useReadyProducts';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToasts } from 'datastage/utils/hooks/useToasts';
// --- External UI Libraries
import DataGrid from '@dtci/spellbook/DataGrid';
// --- Styles
import styles from 'datastage/components/AGGrid/details-table.module.scss';
import { useReadyProductsGridUtils } from './utils/gridUtils';

export const ReadyProductsGrid = () => {
    const agGridApi = useAgGridApi();
    const { isModalOpen, readyProducts, readyProductRange, readyProductSort } = useSelector(
        (s) => s.readyProducts,
    );
    const showSpinner = useSelector((s) => s.spinner.showSpinner);
    const { updateReadyProducts } = useReadyProductsActions();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showErrorToast } = useToasts();

    const { columns } = useReadyProductsGridUtils({
        columnHandlers: [],
    });

    const fetchReadyProductsList = async () => {
        try {
            setLoadingTrue();
            const data = await fetchReadyProductsPage(readyProductRange, readyProductSort);
            if (data?.code) {
                showErrorToast(data?.message || 'Failed to fetch ready products.');
                return;
            }
            updateReadyProducts({
                readyProducts: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                totalItems: data?.totalElements ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
            });
        } catch (error) {
            showErrorToast(error?.message || 'Failed to fetch ready products.');
            console.error('Error fetching ready products:', error);
        } finally {
            setLoadingFalse();
        }
    };

    useEffect(() => {
        if (isModalOpen) {
            fetchReadyProductsList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- load when modal opens only; pagination/sort update via modal + grid utils
    }, [isModalOpen]);

    return (
        <>
            <DataGrid
                className={styles.dataGrid}
                autoSizing
                agGrid={{
                    ref: agGridApi.AgGridApiRef,
                    rowData: readyProducts,
                    headerHeight: 45,
                    defaultColDef: {
                        wrapText: true,
                        autoHeight: true,
                        suppressMenu: true,
                        resizable: true,
                        cellStyle: {
                            paddingTop: '10px',
                            paddingBottom: '10px',
                            wordBreak: 'normal',
                            overflowWrap: 'break-word',
                        },
                    },
                }}
                bordered="none"
                columnDefs={columns}
                sortable={false}
                striped
                noResultsMessage={showSpinner ? 'Loading...' : 'No results'}
            />
        </>
    );
};
